import Header from 'components/Header';
import CustomDataGrid from 'components/CustomDataGrid';
import React, { FC, useEffect, useState } from 'react';
import { GridPaginationModel, GridRenderCellParams, GridColDef } from '@mui/x-data-grid';
import { NoData } from 'components/CustomDataGrid/EmptyStates/NoData';
import { NetworkError } from 'components/CustomDataGrid/EmptyStates/NetworkError';
import { RequestError } from 'components/CustomDataGrid/EmptyStates/RequestError';
import { formatToCurrency } from 'utils';
import { StyledDiv } from 'components/CustomDataGrid/Columns/styled';
import { theme } from 'theme/theme';
import { parse, isValid, format } from 'date-fns';
import { es } from 'date-fns/locale'; // Para manejar fechas en español

const Reference: FC = () => {
  const [filter, setFilter] = useState({
    field: 'fullName',
    value: '',
    operator: 'contains',
  });
  interface Reference {
    referencia: string;
    monto: string;
    nombrePromotor: string;
    fechaCreacion: string;
    status: string;
    refunded: boolean;
    payment_date?: string;
  }

  const [references, setReferences] = useState<Reference[]>([]); // Todos los datos del backend
  const [displayedReferences, setDisplayedReferences] = useState<Reference[]>([]); // Datos paginados para mostrar
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0); // Total de filas
  const [page, setPage] = useState(0); // Página actual
  const [rowsPerPage, setRowsPerPage] = useState(10); // Filas por página
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const [error, setError] = useState(false);

  const initialStateGrid = {
    pagination: {
      paginationModel: {
        page: page,
        pageSize: rowsPerPage,
      },
    },
  };

  const getReferencesColumns = (): GridColDef[] => [
    {
      field: 'referencia',
      headerName: 'Referencia',
      flex: 1.5,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { referencia } = row;
        return <div>{referencia}</div>;
      },
    },
    {
      field: 'tipoReferencia',
      headerName: 'Tipo de referencia',
      flex: 2,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { tipoReferencia } = row;
        return <div>{tipoReferencia}</div>;
      },
    },
    {
      field: 'monto',
      headerName: 'Monto',
      flex: 1,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }: GridRenderCellParams) => {
        const { monto } = row;
        return <div>{formatToCurrency(parseFloat(monto))}</div>;
      },
    },
    {
      field: 'nombrePromotor',
      headerName: 'Nombre del Promotor',
      flex: 2,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { nombrePromotor } = row;
        return <div>{nombrePromotor}</div>;
      },
    },
    {
      field: 'fechaCreacion',
      headerName: 'Fecha de Creación',
      flex: 1.5,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { fechaCreacion } = row;
        return (
          <div>
            {new Date(fechaCreacion).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}
          </div>
        );
      },
    },
    {
      field: 'fechaPago',
      headerName: 'Fecha de Pago',
      flex: 1.5,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { fechaPago } = row;
        if (!fechaPago) {
          return <div>Sin fecha</div>;
        }
        return (
          <div>
            {new Date(fechaPago).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}
          </div>
        );
      },
    },
    {
      field: 'estatus',
      headerName: 'Estatus',
      flex: 1.5,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { status } = row;

        // Mapear el estado a un valor más tangible
        let displayStatus = '';
        switch (status) {
          case 'PENDING':
            displayStatus = 'Esperando pago';
            break;
          case 'REFUNDED':
            displayStatus = 'Reembolsada por caja';
            break;
          case 'REJECTED':
            displayStatus = 'Vencido'; //Rechazado por 48 horas
            break;
          case 'COMPLETE':
            displayStatus = 'Pagada';
            break;
          default:
            displayStatus = 'Desconocido';
        }

        return <div>{displayStatus}</div>;
      },
    },
    /*     {
      field: 'refunded',
      headerName: 'Refunded',
      flex: 0.5,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { refunded } = row;
        return <div>{refunded ? 'Sí' : 'No'}</div>;
      },
    }, */
  ];

  const getData = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_URL}api/v2/reference/details/transactions`,
        {
          method: 'GET',
          redirect: 'follow',
        },
      );
      const result = await response.json();
      console.log('result --->', result);

      const dataWithId = result.data.map((item: any) => ({
        ...item,
        id: item.referencia, // Usa 'referencia' como identificador único
      }));

      console.log('dataWithId --->', dataWithId);

      setReferences(dataWithId || []); // Guarda todos los datos
      setTotal(dataWithId.length || 0); // Total de filas
      setDisplayedReferences(dataWithId.slice(0, rowsPerPage)); // Muestra la primera página
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Actualiza las filas mostradas cuando cambian la página o el tamaño de página
  useEffect(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setDisplayedReferences(references.slice(startIndex, endIndex));
  }, [page, rowsPerPage, references]);

  const handleSearchInput = (text: string) => {
    if (text.length >= 3) {
      const isNumber = !isNaN(Number(text)); // Detecta si es un número
      const isStatus = ['COMPLETE', 'REJECTED', 'PENDING'].includes(text.toUpperCase()); // Detecta estados válidos

      let filteredData = references;

      if (isNumber) {
        // Si es un número, busca en 'referencia' o 'monto'
        filteredData = references.filter(
          (item) => item.referencia.includes(text) || item.monto.toString().includes(text),
        );
      } else {
        // Intenta analizar el texto como una fecha
        const possibleDateFormats = [
          'yyyy-MM-dd', // Formato estándar
          'dd/MM/yyyy', // Formato común
          'd MMMM yyyy', // Formato textual (ej. "7 de abril de 2025")
          'dd/MM', // Día y mes sin año
        ];

        let parsedDate: Date | null = null;

        for (const formatString of possibleDateFormats) {
          const date = parse(text, formatString, new Date(), { locale: es });
          if (isValid(date)) {
            parsedDate = date;
            break;
          }
        }

        if (parsedDate) {
          const formattedInputDate = format(parsedDate, 'yyyy-MM-dd'); // Convierte a 'YYYY-MM-DD'

          // Filtra las referencias por fecha
          filteredData = references.filter((item) => {
            const itemDate = format(new Date(item.fechaCreacion), 'yyyy-MM-dd'); // Convierte a 'YYYY-MM-DD'
            return itemDate === formattedInputDate;
          });
        } else if (isStatus) {
          // Si es un estado válido, busca en 'status'
          filteredData = references.filter(
            (item) => item.status.toUpperCase() === text.toUpperCase(),
          );
        } else {
          // Si es texto, busca en 'nombrePromotor'
          filteredData = references.filter((item) =>
            item.nombrePromotor.toLowerCase().includes(text.toLowerCase()),
          );
        }
      }

      setDisplayedReferences(filteredData.slice(0, rowsPerPage)); // Actualiza los datos mostrados
      setTotal(filteredData.length); // Actualiza el total de filas
      setPage(0); // Reinicia a la primera página
    } else if (text.length === 0) {
      // Reinicia el filtro si el texto está vacío
      setDisplayedReferences(references.slice(0, rowsPerPage)); // Muestra la primera página
      setTotal(references.length); // Total de filas originales
      setPage(0); // Reinicia a la primera página
    }

    // Depuración: verifica qué filtro se está configurando
    console.log('Filtro configurado:', { field: filter.field, value: text });
  };

  const handleChangePage = ({ page, pageSize }: GridPaginationModel) => {
    setPage(page);
    setRowsPerPage(pageSize);
  };

  const handleErrorButton = () => {
    setIsOnlineState(navigator.onLine);
    if (navigator.onLine) {
      getData();
    }
  };

  const hasNoData = references.length === 0 && filter.value.length === 0 && !loading;

  if (!isOnlineState) return <NetworkError handleRetry={handleErrorButton} />;
  if (error) return <RequestError handleRetry={handleErrorButton} />;
  if (hasNoData)
    return (
      <NoData
        message="No hay referencias disponibles"
        buttonLabel="Agregar referencia"
        handleButton={() => console.log('Agregar referencia')}
      />
    );

  return (
    <>
      <Header
        title="Referencias"
        labelSearch="Buscar referencia"
        handleSearch={(text: string) => handleSearchInput(text)}
        trimSearchText={filter.field === 'referenceNumber'}
      />
      <CustomDataGrid
        loading={loading}
        data={displayedReferences as any} // Cast to 'any' to bypass type mismatch
        dataColumns={getReferencesColumns()} // Define esta función para las columnas
        nameFilter={filter}
        title="Listado de referencias"
        sortingMode="server"
        filterMode="client"
        paginationMode="server" // Paginado en el servidor
        totalRows={total}
        handleChangePage={handleChangePage}
        initialState={initialStateGrid}
        page={page}
        pageSize={rowsPerPage}
        pageSizeOptions={[10, 25, 50]} // Opciones de paginado
      />
    </>
  );
};

export default Reference;
